import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  DateRangeFilter,
  UniversalFilter,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { Column, Oem, Model, Handover } from '@/types/common';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import { returnHandover } from '@/services/api/handovers';
import {
  ReportIncidentModal,
  IncidentFormData,
} from './components/ReportIncidentModal';
import { EditIncidentModal } from './components/EditIncidentModal';
import { CreateMaintenanceModal } from '../issues/components/CreateMaintenanceModal';
import ReturnHandoverModal from '../handovers/components/ReturnHandoverModal';
import {
  Incident,
  IncidentStatus,
  IncidentSeverity,
  IncidentListRequest,
  CreateIncidentRequest,
  getListIncidents,
  createIncident,
  markIncidentAsClose,
} from '@/services/api/incidents';
import { getUserEmail, extractApiErrorMessage } from '@/utils';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';

const INCIDENT_TYPE_MAP: Record<string, string> = {
  ACCIDENT_DAMAGE: 'Kecelakaan',
  USAGE_DAMAGE: 'Kerusakan akibat Penggunaan',
  LOST_UNIT: 'Kehilangan Unit',
  MINOR_DAMAGE: 'Kerusakan Ringan',
};

const INCIDENT_TYPE_REQUIRING_RETURN_HANDOVER = 'ACCIDENT_DAMAGE';
// Normalize incident status so the table can handle both API and UI status values.
const getIncidentDisplayStatus = (incident: Incident) => {
  const normalizedStatus = String(incident.status || '').toUpperCase();

  switch (normalizedStatus) {
    case IncidentStatus.CLOSED:
      return 'CLOSED';
    case 'MAINTENANCE_COMPLETED':
    case IncidentStatus.RESOLVED:
      return 'MAINTENANCE_COMPLETED';
    case 'IN_MAINTENANCE':
    case IncidentStatus.IN_PROGRESS:
      return 'IN_MAINTENANCE';
    default:
      return 'OPEN';
  }
};

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface IncidentFilters {
  statuses?: IncidentStatus[];
  severities?: IncidentSeverity[];
  oemIDs?: string[];
  modelIDs?: string[];
  issueTypes?: string[];
}

const IncidentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );

  const [activeFilters, setActiveFilters] = useState<IncidentFilters>({});
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });

  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [handoverForReturn, setHandoverForReturn] = useState<Handover | null>(
    null
  );
  const [pendingIncidentPayload, setPendingIncidentPayload] =
    useState<CreateIncidentRequest | null>(null);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [oemRes, modelRes] = await Promise.all([
          getListOem({ page: '1', size: '100' }),
          getListModel({ page: '1', size: '100' }),
        ]);
        setOems(oemRes.data || []);
        setModels(modelRes.data || []);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);

  const fetchIncidents = useCallback(
    async (
      page: number,
      search: string,
      filters: IncidentFilters,
      size: number,
      range: DateRange
    ) => {
      setIsLoading(true);
      try {
        const params: IncidentListRequest = {
          page: String(page),
          size: String(size),
          search: search || undefined,
          statuses:
            filters.statuses
              ?.map((s) => {
                if (s === IncidentStatus.IN_PROGRESS) return 'IN_MAINTENANCE';
                if (s === IncidentStatus.RESOLVED)
                  return 'MAINTENANCE_COMPLETED';
                return s;
              })
              .join(',') || undefined,
          severities: filters.severities?.join(',') || undefined,
          oemIDs: filters.oemIDs?.join(',') || undefined,
          modelIDs: filters.modelIDs?.join(',') || undefined,
          issueTypes: filters.issueTypes?.join(',') || undefined,
          startDate: range?.from
            ? `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, '0')}-${String(range.from.getDate()).padStart(2, '0')}`
            : undefined,
          endDate: range?.to
            ? `${range.to.getFullYear()}-${String(range.to.getMonth() + 1).padStart(2, '0')}-${String(range.to.getDate()).padStart(2, '0')}`
            : undefined,
        };

        const response = await getListIncidents(params);

        if (response?.data) {
          setIncidents(response.data);
          setTotal(response.pagination?.total || 0);
        } else {
          setIncidents([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('Failed to fetch incidents:', error);
        setIncidents([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchIncidents(currentPage, searchTerm, activeFilters, pageSize, dateRange);
  }, [
    fetchIncidents,
    currentPage,
    searchTerm,
    activeFilters,
    pageSize,
    dateRange,
  ]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM Name',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models Name',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'severities',
        title: 'Severity',
        icon: 'ri-alert-line',
        multiple: true,
        dataSource: [
          { id: IncidentSeverity.LOW, name: 'Low' },
          { id: IncidentSeverity.MEDIUM, name: 'Medium' },
          { id: IncidentSeverity.HIGH, name: 'High' },
          { id: IncidentSeverity.CRITICAL, name: 'Critical' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-flag-line',
        multiple: true,
        dataSource: [
          { id: IncidentStatus.OPEN, name: 'Open' },
          { id: IncidentStatus.IN_PROGRESS, name: 'In Maintenance' },
          { id: IncidentStatus.RESOLVED, name: 'Maintenance Completed' },
          { id: IncidentStatus.CLOSED, name: 'Closed' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
    ],
    [oems, models]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setCurrentPage(1);
  }, []);

  const handleFiltersApply = useCallback((filters: IncidentFilters) => {
    setActionDropdownOpen(null);
    setActiveFilters(filters);
    setCurrentPage(1);
  }, []);

  const handleFiltersClear = useCallback(() => {
    setActionDropdownOpen(null);
    setActiveFilters({});
    setCurrentPage(1);
  }, []);

  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const handleEditClick = useCallback((record: Incident) => {
    setSelectedIncident(record);
    setIsEditModalOpen(true);
    setActionDropdownOpen(null);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleMarkAsClose = useCallback(
    async (incident: Incident) => {
      if (!incident.ID) return;

      try {
        const userEmail = getUserEmail();

        await markIncidentAsClose(incident.ID, {
          updatedBy: userEmail,
        });

        showSuccessToast('Success', 'Incident marked as closed');
        setActionDropdownOpen(null);

        fetchIncidents(
          currentPage,
          searchTerm,
          activeFilters,
          pageSize,
          dateRange
        );
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to mark incident as closed'
        );
        showErrorToast('Error', errorMessage);
      }
    },
    [
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      dateRange,
      fetchIncidents,
    ]
  );

  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActionDropdownOpen(null);
      }
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [actionDropdownOpen]);

  const sortedData = useMemo(() => {
    if (!sortField || !sortDirection) return incidents;

    return [...incidents].sort((a: any, b: any) => {
      if (sortField === 'severity') {
        const severityOrder: Record<string, number> = {
          LOW: 1,
          MEDIUM: 2,
          HIGH: 3,
          CRITICAL: 4,
        };
        const rankA = severityOrder[a[sortField]] || 0;
        const rankB = severityOrder[b[sortField]] || 0;
        return sortDirection === 'asc' ? rankA - rankB : rankB - rankA;
      }

      const aValue = a[sortField];
      const bValue = b[sortField];

      const comparison = String(aValue || '').localeCompare(
        String(bValue || '')
      );
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [incidents, sortField, sortDirection]);

  const handleCreateIncident = async (data: IncidentFormData) => {
    try {
      const userEmail = getUserEmail();
      if (!userEmail) throw new Error('User email not found');

      const payload: CreateIncidentRequest = {
        incidentType: data.issueType || 'ACCIDENT_DAMAGE',
        handoverID: data.handoverID,
        driverID: data.driverID ? Number(data.driverID) : undefined,
        driverCode: data.driverCode,
        driverName: data.driverName,
        clientID: data.clientID,
        clientName: data.clientName,
        incidentDate: data.incidentDate,
        severity: data.severity as string as IncidentSeverity,
        costResponsibility: data.costBearer || 'MITRA',
        damageImageURL: data.imageURL || '',
        policeReportURL: data.policeReportURL || undefined,
        chronology: data.chronology,
        createdBy: userEmail,
        oemID: data.oemID,
      };

      const selectedHandover = data.selectedHandover ?? null;
      const shouldOpenReturnHandover =
        payload.incidentType === INCIDENT_TYPE_REQUIRING_RETURN_HANDOVER &&
        !!selectedHandover;

      setIsReportModalOpen(false);

      if (shouldOpenReturnHandover) {
        setPendingIncidentPayload(payload);
        setHandoverForReturn(selectedHandover);
        setIsReturnModalOpen(true);
      } else {
        await createIncident(payload);
        fetchIncidents(
          currentPage,
          searchTerm,
          activeFilters,
          pageSize,
          dateRange
        );
        showSuccessToast('Success', 'Incident created successfully');
      }
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to create incident');
      showErrorToast('Error', msg);
      throw error;
    }
  };

  const renderIncidentStatus = (incident: Incident) => {
    const incidentStatus = getIncidentDisplayStatus(incident);

    switch (incidentStatus) {
      case 'OPEN':
        return (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50"
            style={{ color: 'var(--state-information-base, #335CFF)' }}
          >
            Open
            <i className="ri-information-line text-[12px]"></i>
          </span>
        );
      case 'IN_MAINTENANCE':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-medium rounded-full bg-[#FFF9EA] text-[#F5B01D]">
            In Maintenance
            <i className="ri-pie-chart-line text-[14px]"></i>
          </span>
        );
      case 'MAINTENANCE_COMPLETED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-[#E8F8F0] text-[#1FC16B]">
            Maintenance Completed
            <i className="ri-information-line text-[12px]"></i>
          </span>
        );
      case 'CLOSED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Closed
          </span>
        );
      default:
        return <span className="text-[14px]">-</span>;
    }
  };

  const columns: Column<Incident>[] = [
    {
      key: 'code',
      title: 'IncidentID',
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[14px]">{value}</span>
      ),
    },
    {
      key: 'linkedMaintenanceCode',
      title: 'MaintenanceID',
      sortable: true,
      render: (value: string, record: Incident) => (
        <span className="text-[14px]">{record.maintenanceCode || value || '-'}</span>
      ),
    },
    {
      key: 'vehicle',
      title: 'Vehicle',
      render: (_, record) => (
        <div className="text-[14px]">
          <div className="font-medium">{record.platNomor}</div>
          <div className="text-gray-500 text-[11px]">
            {record.oemName && record.modelName
              ? `${record.oemName} - ${record.modelName}`
              : record.modelName || record.oemName || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'mitra',
      title: 'Mitra',
      render: (_, record) => {
        const name = record.driverName || '-';
        const code = record.driverCode || '-';
        const driverID = record.driverID;

        return (
          <div
            className={`text-[14px] ${
              driverID
                ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
                : 'text-gray-900 cursor-default'
            }`}
            onClick={(e) => {
              if (driverID) {
                e.stopPropagation();
                const backOfficeUrl =
                  process.env.REACT_APP_BACK_OFFICE_URL ||
                  'https://back-office.dashelectric.co';
                window.open(`${backOfficeUrl}/driver/${driverID}`, '_blank');
              }
            }}
          >
            <div>{name}</div>
            <div className="text-gray-500 text-[11px]">{code}</div>
          </div>
        );
      },
    },
    {
      key: 'incidentType',
      title: 'Incident Type',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{INCIDENT_TYPE_MAP[value] || value}</span>
      ),
    },
    {
      key: 'severity',
      title: 'Severity',
      align: 'center',
      sortable: true,
      render: (value: IncidentSeverity) => (
        <div className="text-center">
          {value ? (
            <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700">
              <span
                className={`h-2 w-2 rounded-full ${
                  value === IncidentSeverity.LOW
                    ? 'bg-green-500'
                    : value === IncidentSeverity.MEDIUM
                      ? 'bg-blue-500'
                      : value === IncidentSeverity.HIGH
                        ? 'bg-orange-500'
                        : value === IncidentSeverity.CRITICAL
                          ? 'bg-red-500'
                          : 'bg-gray-400'
                }`}
              />
              {value}
            </span>
          ) : (
            <span className="text-[14px]">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      sortable: true,
      render: (_: IncidentStatus, record: Incident) => (
        <div className="text-center">{renderIncidentStatus(record)}</div>
      ),
    },
    {
      key: 'incidentDate',
      title: 'Tanggal Kejadian',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">
          {value
            ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '/')
            : '-'}
        </span>
      ),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      sortable: true,
      render: (_: string, record: Incident) => (
        <span className="text-[14px]">{record.createdBy || '-'}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-20',
      align: 'center',
      render: (_: any, record: Incident) => {
        const uniqueId = record.ID;
        const incidentStatus = getIncidentDisplayStatus(record);
        const actions: ActionDropdownAction[] = [];

        actions.push({
          key: 'detail',
          label: 'Detail',
          icon: 'ri-eye-line',
          onClick: () => {
            setActionDropdownOpen(null);
            navigate(`/incidents/${record.ID}`);
          },
          separateAfter: incidentStatus === 'OPEN',
        });

        if (incidentStatus === 'OPEN') {
          actions.push({
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => handleEditClick(record),
            separateAfter: true,
          });
        }

        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for incident ${record.code}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Incident>({
    storageKey: 'incidents',
    columns,
  });

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Incidents
            </h1>
            <p className="text-[14px] text-gray-600">
              Overview and management of all fleet incidents.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => setIsReportModalOpen(true)}
            className="whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-add-line text-[12px] mr-1"></i> Create Incident
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              initialRange={dateRange}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search…"
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {searchTerm && !isLoading && (
        <div className="mb-3 px-2">
          <p className="text-sm text-gray-600">
            Found <span className="font-semibold">{total}</span> result
            {total !== 1 ? 's' : ''} for{' '}
            <span className="font-semibold">'{searchTerm}'</span>
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading incidents...</span>
            </div>
          ) : (
            <TableData
              data={sortedData}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              onRowClick={(record) => navigate(`/incidents/${record.ID}`)}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
      />

      <ReportIncidentModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleCreateIncident}
      />
      <ReturnHandoverModal
        isOpen={isReturnModalOpen}
        handover={handoverForReturn}
        allowedReturnReasons={['ACCIDENT', 'OTHER']}
        successMessage="New Report saved - You can now track the incident!"
        errorMessage="Failed to save Report — please try again"
        onSubmitOverride={async (submitData) => {
          if (pendingIncidentPayload && handoverForReturn) {
            const combinedPayload: CreateIncidentRequest = {
              ...pendingIncidentPayload,
              returnDate: submitData.returnDate,
              returnOdometer: submitData.returnOdometer,
              checklistItems: submitData.checklistItems,
              returnCondition: submitData.returnCondition,
              returnReason: submitData.returnReason,
              returnReasonOther: submitData.returnReasonOther,
              vehicleReturnImageURL: submitData.vehicleReturnImageURL,
              vehicleAndMitraReturnImageURL:
                submitData.vehicleAndMitraReturnImageURL,
              returnBastLink: submitData.returnBastLink,
              returnNotes: submitData.returnNotes,
            };
            await createIncident(combinedPayload);
          } else if (handoverForReturn) {
            await returnHandover(handoverForReturn.ID, submitData);
          }
        }}
        onClose={() => {
          setIsReturnModalOpen(false);
          setHandoverForReturn(null);
          setPendingIncidentPayload(null);
        }}
        onSuccess={() => {
          setIsReturnModalOpen(false);
          setHandoverForReturn(null);
          setPendingIncidentPayload(null);
          fetchIncidents(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
      <EditIncidentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedIncident(null);
        }}
        issue={selectedIncident as any}
        onSuccess={() => {
          fetchIncidents(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
      <CreateMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => {
          setIsMaintenanceModalOpen(false);
          setSelectedIncident(null);
        }}
        issue={selectedIncident as any}
        onSuccess={(maintenanceCode?: string) => {
          if (maintenanceCode && selectedIncident) {
            setIncidents((prev) =>
              prev.map((inc) =>
                inc.ID === selectedIncident.ID
                  ? {
                      ...inc,
                      linkedMaintenanceCode: maintenanceCode,
                      linkedMaintenanceID: 'created',
                    }
                  : inc
              )
            );
          }
          fetchIncidents(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
    </div>
  );
};

export default IncidentsPage;
